import React, { useMemo } from "react";
import {
  Box,
  Link,
  styled,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { format } from "date-fns";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { TaxationEstimateList } from "@rentablo/api/apiv1";
import DownloadIcon from "@icons/myicons/download.svg?react";
import { toCurrency } from "@helpers";
import { SimpleTable } from "@rentablo/components";
import { generateCSVData } from "@features/cashback/components/CashbackSummary/TaxHelper/helpers";

const StyledCSVLink = styled(CSVLink)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
}));

type Props = {
  data?: TaxationEstimateList;
};

export const TaxationSummary = ({ data: taxationData }: Props) => {
  const { t } = useTranslation();
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const csvData = useMemo(
    () => generateCSVData(taxationData?.taxationEstimates),
    [taxationData?.taxationEstimates]
  );

  const period = useMemo(
    () =>
      [taxationData?.estimateFrom, taxationData?.estimateTo]
        .filter(Boolean)
        .map((date) => format(new Date(date!), "dd.MM.yyyy"))
        .join(" - "),
    [taxationData?.estimateFrom, taxationData?.estimateTo]
  );
  const taxableAmount = useMemo(
    () => toCurrency(taxationData?.taxationRelevantAmount, "EUR"),
    [taxationData?.taxationRelevantAmount]
  );
  const downloadLink = useMemo(
    () => (
      <StyledCSVLink data={csvData} filename="steuerschaetzung.csv">
        <Link
          component="span"
          sx={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          <DownloadIcon width="20px" />
          {t("domains.cashback.dashboard.taxHelper.summary.exportData")}
        </Link>
      </StyledCSVLink>
    ),
    [csvData, t]
  );

  const data = useMemo(() => {
    return [
      {
        period: {
          value: period,
          label: t("domains.cashback.dashboard.taxHelper.summary.period"),
        },
        taxableAmount: {
          value: taxableAmount,
          label: t(
            "domains.cashback.dashboard.taxHelper.summary.taxableAmount"
          ),
        },
        export: {
          value: downloadLink,
          label: t("domains.cashback.dashboard.taxHelper.summary.export"),
        },
      },
    ];
  }, [period, taxableAmount, downloadLink, t]);

  const mobileData = useMemo(() => {
    return [
      {
        allData: {
          label: `${t(
            "domains.cashback.dashboard.taxHelper.summary.period"
          )}: ${period}`,
          value: (
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  fontWeight={600}
                >
                  {t(
                    "domains.cashback.dashboard.taxHelper.summary.taxableAmount"
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.success"
                  fontWeight={600}
                >
                  {taxableAmount}
                </Typography>
              </Box>
              {downloadLink}
            </Box>
          ),
        },
      },
    ];
  }, [period, taxableAmount, downloadLink, t]);

  if (!data) {
    return null;
  }

  return (
    <Box mb={6}>
      <SimpleTable data={isLessMd ? mobileData : data} breakpoint="md" />
    </Box>
  );
};
